import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { Platform } from 'react-native';

import type { ActiveTask } from '@/lib/tasks';

/**
 * Generate an iCalendar (.ics) file from the user's active tasks.
 * Compatible with Google Calendar, Apple Calendar, Outlook, Fantastical.
 *
 * Each task becomes a VEVENT — a 30-min "block" at the task's due date with
 * the why-line in the description.
 */
function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toIcsDate(ms: number): string {
  const d = new Date(ms);
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    'Z'
  );
}

function escapeIcs(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

export function buildIcs(tasks: ActiveTask[], propertyName?: string): string {
  const stamp = toIcsDate(Date.now());
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Homestead//Home Manager//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:Homestead${propertyName ? ` — ${escapeIcs(propertyName)}` : ''}`,
    'X-WR-TIMEZONE:UTC',
  ];

  for (const t of tasks) {
    const due = t.due_at ?? Date.now();
    const duration = (t.time_minutes ?? 30) * 60_000;
    const end = due + duration;
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${t.id}@homestead.app`);
    lines.push(`DTSTAMP:${stamp}`);
    lines.push(`DTSTART:${toIcsDate(due)}`);
    lines.push(`DTEND:${toIcsDate(end)}`);
    lines.push(`SUMMARY:${escapeIcs(t.title)}`);
    if (t.why) lines.push(`DESCRIPTION:${escapeIcs(t.why)}`);
    lines.push(`CATEGORIES:Homestead${t.tier ? `,${t.tier}` : ''}`);
    lines.push('BEGIN:VALARM');
    lines.push('TRIGGER:-P1D');
    lines.push('ACTION:DISPLAY');
    lines.push('DESCRIPTION:Reminder');
    lines.push('END:VALARM');
    lines.push('END:VEVENT');
  }

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

export interface ExportResult {
  success: boolean;
  fileUri?: string;
  error?: string;
}

/**
 * Persist + share the .ics on native. On web, trigger a download via a
 * Blob URL — browsers don't expose expo-sharing in a useful way.
 */
export async function exportTasksToIcs(
  tasks: ActiveTask[],
  propertyName?: string
): Promise<ExportResult> {
  if (tasks.length === 0) {
    return { success: false, error: 'No tasks to export.' };
  }
  const ics = buildIcs(tasks, propertyName);
  const fileName = `homestead-${new Date().toISOString().slice(0, 10)}.ics`;

  if (Platform.OS === 'web') {
    try {
      // eslint-disable-next-line no-undef
      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      // eslint-disable-next-line no-undef
      const url = URL.createObjectURL(blob);
      // eslint-disable-next-line no-undef
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      // eslint-disable-next-line no-undef
      document.body.appendChild(a);
      a.click();
      a.remove();
      // eslint-disable-next-line no-undef
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return { success: true };
    } catch (err) {
      return { success: false, error: String(err) };
    }
  }

  try {
    const dir = new FileSystem.Directory(FileSystem.Paths.cache, 'exports');
    if (!dir.exists) dir.create({ intermediates: true });
    const file = new FileSystem.File(dir, fileName);
    file.write(ics);
    const canShare = await Sharing.isAvailableAsync();
    if (canShare) {
      await Sharing.shareAsync(file.uri, {
        mimeType: 'text/calendar',
        dialogTitle: 'Export Homestead calendar',
        UTI: 'public.calendar-event',
      });
    }
    return { success: true, fileUri: file.uri };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}
